import React from 'react';
import DisclaimerText from './DisclaimerText';

const LongTermHelpModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      ></div>

      {/* Modal */}
      <div className="flex items-center justify-center min-h-screen p-4">
        <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-indigo-600 px-6 py-4 flex justify-between items-center">
            <h2 className="text-2xl font-bold text-white">📖 How to Use: Long-Term Investment Model</h2>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="px-6 py-6 space-y-6">
            {/* Introduction */}
            <section>
              <h3 className="text-xl font-semibold text-gray-800 mb-3">🎯 Overview</h3>
              <p className="text-gray-700">
                The Long-Term Investment Model identifies stocks recovering from significant downtrends using a 
                systematic bullish reversal pattern approach. This model is designed for position traders and 
                long-term investors (6-18 months holding period) looking for high-reward recovery opportunities 
                with well-defined risk management.
              </p>
            </section>

            {/* Investment Strategy */}
            <section>
              <h3 className="text-xl font-semibold text-gray-800 mb-3">📈 Investment Strategy Explained</h3>
              <p className="text-gray-700 mb-3">
                This model uses a three-stage pattern recognition system:
              </p>
              <div className="space-y-3">
                <div className="p-3 bg-blue-50 border-l-4 border-blue-500">
                  <h4 className="font-semibold text-blue-900">Stage 1: Major High</h4>
                  <p className="text-blue-800 text-sm">
                    The stock's peak price in the past 2 years, representing the maximum investor confidence point.
                  </p>
                </div>
                <div className="p-3 bg-orange-50 border-l-4 border-orange-500">
                  <h4 className="font-semibold text-orange-900">Stage 2: Trend Low (The Opportunity)</h4>
                  <p className="text-orange-800 text-sm">
                    The most recent significant low, representing maximum pessimism. This is where value investors look for entries.
                  </p>
                </div>
                <div className="p-3 bg-green-50 border-l-4 border-green-500">
                  <h4 className="font-semibold text-green-900">Stage 3: Recovery Phase (Current Price)</h4>
                  <p className="text-green-800 text-sm">
                    The stock has started recovering from its low with improving fundamentals (RSI above 20, increasing volume).
                  </p>
                </div>
              </div>
            </section>

            {/* Screening Criteria */}
            <section>
              <h3 className="text-xl font-semibold text-gray-800 mb-3">🔍 Screening Criteria</h3>
              <p className="text-gray-700 mb-2">
                All stocks must pass these three conditions to qualify:
              </p>
              <div className="space-y-2">
                <div>
                  <h4 className="font-semibold text-gray-700">✅ 1. Confirmed Downtrend</h4>
                  <ul className="text-gray-600 text-sm ml-6 list-disc">
                    <li>Price below 200-week moving average</li>
                    <li>200-week MA is downward sloping</li>
                    <li>RSI below 50 (weak momentum)</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-700">✅ 2. RSI Recovery Zone (20-50)</h4>
                  <ul className="text-gray-600 text-sm ml-6 list-disc">
                    <li>RSI above 20: Not deeply oversold anymore</li>
                    <li>RSI below 50: Not yet in bullish momentum</li>
                    <li>This is the "sweet spot" for accumulation</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-700">✅ 3. Volume Confirmation</h4>
                  <ul className="text-gray-600 text-sm ml-6 list-disc">
                    <li>Current volume is 20% above 20-week average</li>
                    <li>Indicates renewed interest from institutional investors</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Configure Parameters */}
            <section>
              <h3 className="text-xl font-semibold text-gray-800 mb-3">⚙️ Configure Analysis Parameters</h3>
              
              <div className="space-y-3">
                <div>
                  <h4 className="font-semibold text-gray-700">📊 Stock Index</h4>
                  <p className="text-gray-600 text-sm">
                    Select which stock universe to scan (NIFTY 50, NIFTY 200, NIFTY BANK, etc.). 
                    You can also upload a custom CSV file with your own list of stocks.
                  </p>
                </div>

                <div>
                  <h4 className="font-semibold text-gray-700">📅 Analysis Period (Default: 1 year)</h4>
                  <p className="text-gray-600 text-sm">
                    Historical period to analyze for patterns. Options: 1-5 years.
                    <br/>• <strong>1-2 years:</strong> Recent trends, faster recovery
                    <br/>• <strong>3-5 years:</strong> Longer-term trends, deeper patterns
                  </p>
                </div>
              </div>
            </section>

            {/* How to Upload CSV */}
            <section>
              <h3 className="text-xl font-semibold text-gray-800 mb-3">📤 Upload Custom Stock List (CSV)</h3>
              <p className="text-gray-700 mb-2">
                You can analyze your own custom list of stocks by uploading a CSV file:
              </p>
              <ol className="list-decimal list-inside text-gray-600 space-y-1 ml-4">
                <li>Create a CSV file with a column named <strong>"Symbol"</strong></li>
                <li>Add stock symbols in the format: <code className="bg-gray-100 px-2 py-1 rounded">RELIANCE.NSE</code>, <code className="bg-gray-100 px-2 py-1 rounded">TCS.NSE</code></li>
                <li>Click the "Upload CSV File" button and select your file</li>
                <li>The model will analyze your custom stock list</li>
              </ol>
              <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded">
                <p className="text-sm text-blue-800">
                  <strong>Example CSV format:</strong>
                  <br/>
                  <code className="text-xs bg-white px-2 py-1 rounded mt-1 inline-block">
                    Symbol<br/>
                    RELIANCE.NSE<br/>
                    TCS.NSE<br/>
                    INFY.NSE
                  </code>
                </p>
              </div>
            </section>

            {/* Understanding Results */}
            <section>
              <h3 className="text-xl font-semibold text-gray-800 mb-3">📊 Understanding the Results</h3>
              <p className="text-gray-700 mb-2">
                Each qualifying stock will display a complete trade setup:
              </p>
              <ul className="list-disc list-inside text-gray-600 space-y-1 ml-4">
                <li><strong>Symbol:</strong> Stock ticker symbol</li>
                <li><strong>Current Price:</strong> Latest market price</li>
                <li><strong>Entry Price:</strong> Recommended entry point (current market price)</li>
                <li><strong>Target Price:</strong> 80% recovery from Trend Low to Trend High</li>
                <li><strong>Stop Loss:</strong> 10% below Trend Low (risk management)</li>
                <li><strong>Potential Gain:</strong> Absolute profit potential in ₹</li>
                <li><strong>Gain %:</strong> Expected return percentage</li>
                <li><strong>Risk/Reward:</strong> Ratio of potential gain to potential loss (higher is better)</li>
              </ul>
            </section>

            {/* Trading Action */}
            <section>
              <h3 className="text-xl font-semibold text-gray-800 mb-3">🎯 Taking Action</h3>
              <p className="text-gray-700 mb-2">
                For each stock in the results:
              </p>
              <ol className="list-decimal list-inside text-gray-600 space-y-1 ml-4">
                <li>Review the Risk/Reward ratio (ideally 1.5 or higher)</li>
                <li>Click the <strong>"Trade"</strong> button to open the GTT Order modal</li>
                <li>The entry, target, and stop-loss prices will be pre-filled</li>
                <li>Adjust quantity and fine-tune prices if needed</li>
                <li>Place your GTT order (valid for 1 year)</li>
                <li>You can also <strong>download the results</strong> as an Excel file for further analysis</li>
              </ol>
            </section>

            {/* Tips */}
            <section className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-yellow-900 mb-2">💡 Pro Tips</h3>
              <ul className="list-disc list-inside text-yellow-800 space-y-1 text-sm ml-4">
                <li>Focus on stocks with Risk/Reward ratio above 1.5 for better asymmetric bets</li>
                <li>Use GTT orders to automate entries and exits (no daily monitoring needed)</li>
                <li>This strategy works best in CNC (delivery) mode for long-term positions</li>
                <li>Consider position sizing: higher R:R = can risk smaller % of capital</li>
                <li>Track your trades: download results and monitor recovery progress monthly</li>
                <li>Be patient: Long-term recovery can take 6-18 months to play out</li>
              </ul>
            </section>

            {/* Risk Warning */}
            <section className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h3 className="text-lg font-semibold text-red-900 mb-2">⚠️ Risk Disclaimer</h3>
              <p className="text-red-800 text-sm">
                This model identifies technical patterns based on historical data. Not all stocks will recover successfully. 
                Always do your own fundamental research, understand the business, and never invest more than you can afford to lose. 
                Past performance does not guarantee future results.
              </p>
            </section>

            {/* Platform Disclaimer */}
            <DisclaimerText />
          </div>

          {/* Footer */}
          <div className="sticky bottom-0 bg-gray-50 px-6 py-4 border-t">
            <button
              onClick={onClose}
              className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white px-6 py-2 rounded-md font-medium transition-colors"
            >
              Got it! Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LongTermHelpModal;

